/**
 * Declarative configuration for the map settings sheet.
 *
 * The sheet has three tabs. "Base map" and "Overlays" are curated lists with
 * copy that does not live on the layer definitions, so they are declared here.
 * "Waypoints" is derived from the layer registry so that every marker type
 * keeps showing up without anyone having to remember to add it.
 *
 * A layer must appear in exactly one place. Anything listed in
 * `OVERLAY_SECTIONS` is excluded from the waypoint groups — see
 * `mapSettingsConfig.test.ts`, which asserts that the union of the two still
 * covers everything a user could reach before this sheet was rebuilt.
 */

import activityAreasThumbnail from './assets/ActivityAreas.png';
import aidsToNavigationThumbnail from './assets/AidstoNavigation.png';
import aisThumbnail from './assets/AIS.png';
import environmentZonesThumbnail from './assets/EnvironmentZones.png';
import heatmapsThumbnail from './assets/Heatmaps.png';
import restrictedAreasThumbnail from './assets/RestrictedAreas.png';
import speedZonesThumbnail from './assets/SpeedZones.png';
import underwaterMapsThumbnail from './assets/UnderwaterMaps.png';

export type MapSettingsTab = 'base-map' | 'overlays' | 'waypoints';

export const MAP_SETTINGS_TABS: { id: MapSettingsTab; label: string }[] = [
  { id: 'base-map', label: 'Base map' },
  { id: 'overlays', label: 'Overlays' },
  { id: 'waypoints', label: 'Waypoints' },
];

export interface OverlayLayerConfig {
  /** Key into `getLayerDefinitions()`. */
  layerName: string;
  title: string;
  subtitle: string;
  /** Preview artwork. Falls back to a placeholder tile when absent. */
  imageUrl?: string;
  /** Gated behind a subscription; shows the star and opens the paywall. */
  subscriberOnly?: boolean;
}

export interface OverlaySection {
  title: string;
  items: OverlayLayerConfig[];
}

export const OVERLAY_SECTIONS: OverlaySection[] = [
  {
    title: 'Activity',
    items: [
      {
        layerName: 'dynamicActivityHeatmap',
        title: 'Community heatmap',
        subtitle: 'Popular routes and spots',
        imageUrl: heatmapsThumbnail,
      },
      {
        layerName: 'ais',
        title: 'Vessel tracking',
        subtitle: 'Global AIS network',
        imageUrl: aisThumbnail,
      },
    ],
  },
  {
    title: 'Regulations',
    items: [
      {
        layerName: 'navigation',
        title: 'Aids to navigation',
        subtitle: 'Buoys, beacons and markers',
        imageUrl: aidsToNavigationThumbnail,
      },
      {
        layerName: 'restrictedArea',
        title: 'Restricted areas',
        subtitle: 'Where access is limited or prohibited',
        imageUrl: restrictedAreasThumbnail,
      },
      {
        layerName: 'speedLimitationArea',
        title: 'Speed zones',
        subtitle: 'Where you need to slow down',
        imageUrl: speedZonesThumbnail,
      },
      {
        layerName: 'environmentArea',
        title: 'Environmental zones',
        subtitle: 'Reserves with rules on what you can do',
        imageUrl: environmentZonesThumbnail,
      },
      {
        layerName: 'activityArea',
        title: 'Activity areas',
        subtitle: 'Zones designated for watersports',
        imageUrl: activityAreasThumbnail,
      },
    ],
  },
  {
    title: 'Terrain',
    items: [
      {
        layerName: 'wmsbathymetry',
        title: 'Underwater maps',
        subtitle: 'Beneath the surface (limited preview)',
        imageUrl: underwaterMapsThumbnail,
        subscriberOnly: true,
      },
    ],
  },
];

const allOverlayItems = OVERLAY_SECTIONS.reduce<OverlayLayerConfig[]>(
  (items, section) => items.concat(section.items),
  [],
);

/** Every layer claimed by the Overlays tab, so Waypoints can skip them. */
export const OVERLAY_LAYER_NAMES: ReadonlySet<string> = new Set(
  allOverlayItems.map((item) => item.layerName),
);

const SUBSCRIBER_ONLY_LAYER_NAMES: ReadonlySet<string> = new Set(
  allOverlayItems
    .filter((item) => item.subscriberOnly)
    .map((item) => item.layerName),
);

export const isSubscriberOnlyLayer = (layerName: string): boolean =>
  SUBSCRIBER_ONLY_LAYER_NAMES.has(layerName);

/** The shape `mapBaseLayerDefinitions` entries share. That module is JS. */
export interface BaseMapDefinition {
  displayName: string;
  iconImage: string;
  subscriberOnly?: boolean;
}

/** Supporting copy for the base map rows; titles come from `displayName`. */
export const BASE_MAP_SUBTITLES: Record<string, string> = {
  satellite: 'Detailed satellite imagery',
  terrain: 'Topographic land and coastline',
  nautical: 'Australia and USA only',
};

/**
 * Marker categories, keyed by the `menuOptions.owningMenu` already carried by
 * every layer definition. Titles are the ones the previous category rows used.
 */
export const WAYPOINT_GROUPS: { owningMenu: string; title: string }[] = [
  {
    owningMenu: 'marine-points-of-interest',
    title: 'Marine points of interest',
  },
  { owningMenu: 'marine-facilities', title: 'Marine facilities' },
  { owningMenu: 'general-services', title: 'General services' },
  { owningMenu: 'safety', title: 'Safety' },
];

export interface WaypointLayerIcon {
  baseShape: () => unknown;
  iconColor?: string;
  markerColor?: string;
}

export interface WaypointLayer {
  layerName: string;
  displayName: string;
  icon: WaypointLayerIcon;
}

export interface WaypointGroup {
  owningMenu: string;
  title: string;
  layers: WaypointLayer[];
}

interface LayerDefinition {
  displayName: string;
  icon: WaypointLayerIcon;
  menuOptions?: { visible?: boolean; owningMenu?: string };
}

/**
 * Group the marker layers for the Waypoints tab. Same visibility filter the
 * old per-category pages used, minus anything the Overlays tab now owns.
 */
export const buildWaypointGroups = (
  definitions: Record<string, LayerDefinition>,
): WaypointGroup[] =>
  WAYPOINT_GROUPS.map((group) => ({
    ...group,
    layers: Object.keys(definitions)
      .filter((layerName) => {
        const { menuOptions } = definitions[layerName];
        return (
          !!menuOptions &&
          !!menuOptions.visible &&
          menuOptions.owningMenu === group.owningMenu &&
          !OVERLAY_LAYER_NAMES.has(layerName)
        );
      })
      .map((layerName) => ({
        layerName,
        displayName: definitions[layerName].displayName,
        icon: definitions[layerName].icon,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName)),
  }));
